import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { postTelemetryEvents } from './api';
import {
  clearTelemetryVisitorId,
  getOrCreateTelemetryVisitorId,
  hasCookieConsentForTelemetry,
} from './visitor';

const FLUSH_MS = 2000;
const MAX_PATH = 512;

const CONSENT_EVENT = 'bmcs-cookie-consent';

function normalizePath(pathname: string): string {
  const p = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return p.length <= MAX_PATH ? p : `${p.slice(0, MAX_PATH - 1)}…`;
}

/**
 * Records public SPA page views for anonymous visitors (cookie consent required).
 * Must render under React Router.
 */
const TelemetryTracker: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const queueRef = useRef<{ path: string }[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (user) return;
    if (!hasCookieConsentForTelemetry()) return;
    const visitorId = getOrCreateTelemetryVisitorId();
    if (!visitorId) return;
    const batch = queueRef.current.splice(0, queueRef.current.length);
    if (batch.length === 0) return;
    postTelemetryEvents(visitorId, batch).catch(() => {
      // non-blocking; avoid console noise in production
    });
  }, [user]);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      flush();
    }, FLUSH_MS);
  }, [flush]);

  useEffect(() => {
    const onConsent = () => {
      if (!hasCookieConsentForTelemetry()) {
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        queueRef.current = [];
        clearTelemetryVisitorId();
        return;
      }
      scheduleFlush();
    };
    window.addEventListener(CONSENT_EVENT, onConsent);
    return () => window.removeEventListener(CONSENT_EVENT, onConsent);
  }, [scheduleFlush]);

  useEffect(() => {
    const onHide = () => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      flush();
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') onHide();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onHide);
    };
  }, [flush]);

  useEffect(() => {
    if (user) return;
    if (location.pathname.startsWith('/dashboard')) return;
    if (!hasCookieConsentForTelemetry()) return;

    const path = normalizePath(location.pathname);
    queueRef.current.push({ path });
    scheduleFlush();
  }, [location.pathname, user, scheduleFlush]);

  return null;
};

export default TelemetryTracker;
